import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { take } from 'rxjs/operators';
import { StorageService } from '../storage/storage.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Injectable, Inject, PLATFORM_ID, Optional } from '@angular/core';
import { isPlatformBrowser } from "@angular/common";
import { TranslocoService } from '@jsverse/transloco';
import { API_URL, APP_KEY_NAME } from '../../app.constants';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private APP_KEY: string | null = null;
  private errors: any = null;
  private companyCache: any = null;
  private companyCacheTimestamp: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minuti in millisecondi
  private hasRedirectedForMissingAppKey = false;

  constructor(private translocoService: TranslocoService, private route: ActivatedRoute, private http: HttpClient, private storageService: StorageService, private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Optional() @Inject('message') public message: string) {
    if (this.isValidAppKey(this.message)) {
      this.APP_KEY = this.message;

      if (isPlatformBrowser(this.platformId) && this.storageService.getAppKey() !== this.message) {
        this.storageService.setCookie(APP_KEY_NAME, this.message);
      }
    }

    this.translocoService.selectTranslateObject('api_error').subscribe((translations) => this.errors = translations);
  }

  private isValidAppKey(appKey: string | null | undefined): boolean {
    return !!appKey && appKey.length === 32;
  }

  private navigateToMissingAppKeyError(): void {
    if (!isPlatformBrowser(this.platformId) || this.hasRedirectedForMissingAppKey) {
      return;
    }

    this.hasRedirectedForMissingAppKey = true;
    this.router.navigate(['error'], { queryParams: { code: 'APP_KEY_NOT_FOUND' } });
  }

  private resolveAppKeyOrFail(): string | null {
    const appKey = this.APP_KEY || this.storageService.getAppKey();
    if (this.isValidAppKey(appKey)) {
      return appKey;
    }

    this.navigateToMissingAppKeyError();
    return null;
  }

  private _handleErrors(error: HttpErrorResponse): Observable<any> {
    let errorMessage: string;
    if (error instanceof Error) {
      errorMessage = error.message;
    } else if (error.status) {
      if (this.errors.statuses['code-' + error.status]) {
        errorMessage = this.errors.statuses['code-' + error.status];
      } else {
        errorMessage = this.errors.general_with_code.replace('%code%', error.status);
      }
    } else
      errorMessage = this.errors.general;
    return throwError(() => errorMessage);
  }

  /**
   * Send a request to register an Insight event
   * @param payload insight event payload
   * @returns an Observable with the result of the request
   */
  sendInsight(payload: any): Observable<any> {
    return new Observable<any>((observer) => {
      this.http.post<any>(API_URL + `/sendinsight`, JSON.stringify(payload), {
        headers: {
          'Content-Type': 'application/json'
        }
      }).pipe(catchError((error) => this._handleErrors(error))).subscribe({
        next: (response) => {
          if (!response.success || response.error) {
            observer.error(this.errors.response_general);
          } else {
            observer.next(response);
          }
        },
        error: (error) => observer.error(error)
      });
    });
  }

  /**
   * Get company information
   * @returns an Observable with the result of the request
   */
  getCompany(): Observable<any> {
    const appKey = this.resolveAppKeyOrFail();
    if (!appKey) {
      return throwError(() => 'APP_KEY_NOT_FOUND');
    }

    return new Observable<any>((observer) => {
      this.route.queryParams.pipe(take(1)).subscribe(params => {
        const now = Date.now();
        const isCacheValid = this.companyCache && (now - this.companyCacheTimestamp) < this.CACHE_DURATION;
        
        // Verifica se usare la cache
        if (!params['reload'] && isCacheValid) {
          console.log("Uso cache per /popolapwaguest");
          observer.next(this.companyCache);
          observer.complete();
          return;
        }

        let requestParams = {};
        if (params['reload']) { //bypass cache
          requestParams = { timestamp: Date.now() };
          console.log("Bypass cache per /popolapwaguest");
          this.clearCompanyCache(); // Invalida la cache esistente
        }

        this.http.get<any>(API_URL + `/popolapwaguest/${appKey}`, {
          params: requestParams,
          headers: {
            'Content-Type': 'application/json'
          }
        }).pipe(catchError((error) => this._handleErrors(error))).subscribe({
          next: (response) => {
            if (!response.success || response.error) {
              observer.error(this.errors.response_general);
            } else {
              if (response.data.active) {
                // Salva in cache la risposta
                this.companyCache = response;
                this.companyCacheTimestamp = Date.now();
                
                observer.next(response);
                observer.complete();
              } else {
                this.router.navigate(['/error'], { queryParams: { code: 'PWA_SUSPENDED' } });
                observer.error(this.errors.company_not_active);
                observer.complete();
              }
            }
          },
          error: (error) => { 
            observer.error(error);
            observer.complete();
          },
        });
      });
    });
  }

  /**
   * Clear the company cache
   */
  clearCompanyCache(): void {
    this.companyCache = null;
    this.companyCacheTimestamp = 0;
  }

  /**
   * Used to update the notification status of the user.
   * @param payload data to send to the server
   * @returns an Observable with the result of the request
   */
  updateNotificationState(payload: any): Observable<any> {
    return new Observable<any>((observer) => {
      this.http.post<any>(API_URL + `/updatestate`, JSON.stringify(payload), {
        headers: {
          'Content-Type': 'application/json'
        }
      }).pipe(catchError((error) => this._handleErrors(error))).subscribe({
        next: (response) => {
          if (!response.success || response.error) {
            observer.error(this.errors.response_general);
          } else {
            observer.next(response);
          }
        },
        error: (error) => observer.error(error)
      });
    });
  }

  /**
   * Update FCM token for the user
   * @param payload payload to send to the server
   * @returns an Observable with the result of the request
   */
  updateNotificationToken(payload: any): Observable<any> {
    return new Observable<any>((observer) => {
      this.http.post<any>(API_URL + `/updatepush`, JSON.stringify(payload), {
        headers: {
          'Content-Type': 'application/json'
        }
      }).pipe(catchError((error) => this._handleErrors(error))).subscribe({
        next: (response) => {
          if (!response.success || response.error) {
            observer.error(this.errors.response_general);
          } else {
            observer.next(response);
          }
        },
        error: (error) => observer.error(error)
      });
    });
  }

  /**
   * Get the user identifier for the specified user.
   * @returns an Observable with the result of the request
   */
  getUserUID(): Observable<any> {
    return new Observable<any>((observer) => {
      this.http.get<string>(API_URL + `/newuid`, {
        headers: {
          'Content-Type': 'application/json'
        }
      }).pipe(catchError((error) => this._handleErrors(error))).subscribe({
        next: (response) => {
          if (!response.success || response.error) {
            observer.error(this.errors.response_general);
          } else {
            observer.next(response);
          }
        },
        error: (error) => observer.error(error)
      });
    });
  }

  /**
   * Send a request to register a support request
   * @param payload payload to send to the server
   * @returns an Observable with the result of the request
   */
  sendSupportRequest(payload: any): Observable<any> {
    return new Observable<any>((observer) => {
      this.http.post<any>(API_URL + `/supportrequest`, JSON.stringify(payload), {
        headers: {
          'Content-Type': 'application/json'
        }
      }).pipe(catchError((error) => this._handleErrors(error))).subscribe({
        next: (response) => {
          if (!response.success || response.error) {
            observer.error(this.errors.response_general);
          } else {
            observer.next(response);
          }
        },
        error: (error) => observer.error(error),
      });
    });
  }

  /**
   * Send a contact form to the company
   * @param payload payload to send to the server
   * @returns an Observable with the result of the request
   */
  contactCompany(payload: any): Observable<any> {
    return new Observable<any>((observer) => {
      this.http.post<any>(API_URL + `/contactcompany`, JSON.stringify({ ...payload, appkey: this.storageService.getAppKey() }), {
        headers: {
          'Content-Type': 'application/json'
        }
      }).pipe(catchError((error) => this._handleErrors(error))).subscribe({
        next: (response) => {
          if (!response.success || response.error) {
            observer.error(this.errors.response_general);
          } else {
            observer.next(response);
          }
        },
        error: (error) => observer.error(error),
      });
    });
  }

  sendWelcomePushNotification(token: string): Observable<any> {
    return new Observable<any>((observer) => {
      this.http.post<any>(API_URL + `/sendpushwelcome`, JSON.stringify({ token: token, appkey: this.storageService.getAppKey() }), {
        headers: {
          'Content-Type': 'application/json'
        }
      }).pipe(catchError((error) => this._handleErrors(error))).subscribe({
        next: (response) => {
          if (!response.success || response.error) {
            observer.error(this.errors.response_general);
          } else {
            observer.next(response);
          }
        },
        error: (error) => observer.error(error),
      });
    });
  }

  /**
   * Update lang for the client
   * @param lang lang code
   * @returns an Observable with the result of the request
   */
  updateLang(uid: string, lang: string) {
    return new Observable<any>((observer) => {
      this.http.post<any>(API_URL + `/updatelang`, JSON.stringify({ lang, uid, appkey: this.storageService.getAppKey() }), {
        headers: {
          'Content-Type': 'application/json'
        }
      }).pipe(catchError((error) => this._handleErrors(error))).subscribe({
        next: (response) => {
          if (!response.success || response.error) {
            observer.error(this.errors.response_general);
          } else {
            observer.next(response);
          }
        },
        error: (error) => observer.error(error),
      });
    });
  }
}
